import type { Organisation } from '@esf/shared'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

interface AdminDashboardStats {
  totalOrganisations: number
  totalAssets: number
  totalUsers: number
  totalWorkOrders: number
  totalIoTDevices: number
  byOrganisation: Array<{
    id: string
    name: string
    slug: string
    assetCount: number
    userCount: number
    workOrderCount: number
    iotDeviceCount: number
  }>
}

interface AdminDashboardPageProps {
  stats: AdminDashboardStats | null
  organisations: Organisation[]
  selectedOrgId: string | null
  onSelectOrg: (orgId: string | null) => void
  onRefresh: () => void
  onNavigateToOrg: (orgId: string) => void
  isLoading: boolean
}

export default function AdminDashboardPage({
  stats,
  organisations,
  selectedOrgId,
  onSelectOrg,
  onRefresh,
  onNavigateToOrg,
  isLoading,
}: AdminDashboardPageProps) {
  const enabledOrgs = organisations.filter(o => o.enabled !== false)

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
          <p className="text-sm text-gray-500 mt-1">
            Aggregated metrics across all organisations
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            className="input"
            value={selectedOrgId || ''}
            onChange={(e) => onSelectOrg(e.target.value || null)}
          >
            <option value="">All Organisations</option>
            {enabledOrgs.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
          <button onClick={onRefresh} className="btn btn-secondary">
            {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading platform metrics...</div>
        </div>
      ) : (
        <>
          {/* Platform-wide KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="kpi">
              <div className="kpi-icon bg-esf-navy/10 text-esf-navy">
                {renderIcon('M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4')}
              </div>
              <div className="kpi-title">Organisations</div>
              <div className="kpi-value">{stats?.totalOrganisations || 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-icon bg-blue-100 text-blue-600">
                {renderIcon('M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10')}
              </div>
              <div className="kpi-title">Total Assets</div>
              <div className="kpi-value">{stats?.totalAssets?.toLocaleString() || 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-icon bg-purple-100 text-purple-600">
                {renderIcon('M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z')}
              </div>
              <div className="kpi-title">Total Users</div>
              <div className="kpi-value">{stats?.totalUsers || 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-icon bg-amber-100 text-amber-600">
                {renderIcon('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01')}
              </div>
              <div className="kpi-title">Work Orders</div>
              <div className="kpi-value">{stats?.totalWorkOrders || 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-icon bg-green-100 text-green-600">
                {renderIcon('M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z')}
              </div>
              <div className="kpi-title">IoT Devices</div>
              <div className="kpi-value">{stats?.totalIoTDevices || 0}</div>
            </div>
          </div>

          {/* Organisation Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Organisation Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Organisation</th>
                    <th className="text-right">Assets</th>
                    <th className="text-right">Users</th>
                    <th className="text-right">Work Orders</th>
                    <th className="text-right">IoT Devices</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.byOrganisation || []).map(org => (
                    <tr key={org.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-esf-navy/10 flex items-center justify-center text-esf-navy font-bold text-sm">
                            {org.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{org.name}</div>
                            <div className="text-xs text-gray-500 font-mono">{org.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-right font-medium">{org.assetCount.toLocaleString()}</td>
                      <td className="text-right">{org.userCount}</td>
                      <td className="text-right">{org.workOrderCount}</td>
                      <td className="text-right">{org.iotDeviceCount}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => onNavigateToOrg(org.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!stats?.byOrganisation || stats.byOrganisation.length === 0) && (
                    <tr>
                      <td colSpan={6} className="text-center text-gray-500 py-8">
                        No organisation data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
